import { Card, Divider, Progress, Space, Statistic, Tag, Typography } from 'antd';
import { CalendarOutlined, TrophyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { ParticipantEngagementCycleResponse } from '../../types/engagement.types';

dayjs.locale('pt-br');

const { Text } = Typography;

const PRIZE_LABELS: Record<number, string> = { 1: '1º', 2: '2º', 3: '3º', 4: '4º', 5: '5º' };

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'Aberto', color: 'success' },
  VALIDATING: { label: 'Em validação', color: 'warning' },
  CLOSED: { label: 'Encerrado', color: 'default' },
};

interface EngagementPointsCardProps {
  data: ParticipantEngagementCycleResponse;
}

export const EngagementPointsCard = ({ data }: EngagementPointsCardProps) => {
  const total = data.participant.totalPoints;
  const threshold = data.cycle.minimumPrizePoints;
  const percent = threshold > 0 ? Math.min((total / threshold) * 100, 100) : 0;
  const { cycle } = data;
  const statusInfo = STATUS_LABELS[cycle.status] ?? { label: cycle.status, color: 'default' };
  const hasPrizes = cycle.prizes.length > 0;
  const hasLotteryInfo = cycle.lotteryDrawNumber || cycle.lotteryDrawDate;

  return (
    <Card title="Programa de Engajamento" style={{ marginBottom: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
          <Statistic
            title="Pontos no ciclo"
            value={total}
            prefix={<TrophyOutlined style={{ color: '#2E2B81' }} />}
            valueStyle={{ fontSize: 36, fontWeight: 700, color: '#2E2B81' }}
          />
          <div style={{ display: 'flex', gap: 24, paddingBottom: 6 }}>
            <Statistic title="Por pesquisas" value={data.participant.surveyPoints} valueStyle={{ fontSize: 20, color: '#595959' }} />
            <Statistic title="Por indicações" value={data.participant.referralPoints} valueStyle={{ fontSize: 20, color: '#595959' }} />
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text type="secondary">
              Mínimo para elegibilidade: {threshold} pontos
            </Text>
            {!data.participant.eligibleForPrize && threshold > total && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Faltam {threshold - total} pontos
              </Text>
            )}
          </div>
          <Progress
            percent={Number(percent.toFixed(1))}
            strokeColor={data.participant.eligibleForPrize ? '#00984A' : '#2E2B81'}
            trailColor="#f0f0f0"
          />
        </div>

        {data.participant.eligibleForPrize ? (
          <Tag color="#00984A" style={{ color: '#fff' }}>Elegibilidade atingida neste ciclo</Tag>
        ) : (
          <Tag color="#FFC200" style={{ color: '#141414' }}>Abaixo do mínimo de elegibilidade neste ciclo</Tag>
        )}

        <Divider style={{ margin: '4px 0' }} />

        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Space wrap>
            <Text type="secondary">
              <CalendarOutlined style={{ marginRight: 4 }} />
              {dayjs(cycle.startsAt).format('DD/MM/YYYY')} a {dayjs(cycle.endsAt).format('DD/MM/YYYY')}
            </Text>
            <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
          </Space>

          <Text type="secondary">
            {cycle.surveyPoints} pt{cycle.surveyPoints !== 1 ? 's' : ''} por pesquisa
            {' · '}
            {cycle.referralPoints} pt{cycle.referralPoints !== 1 ? 's' : ''} por indicação
          </Text>

          {hasPrizes && (
            <Space wrap>
              <TrophyOutlined style={{ color: '#FFC200' }} />
              {cycle.prizes.map((p) => (
                <Text key={p.prizeRank} type="secondary">
                  {PRIZE_LABELS[p.prizeRank] ?? `${p.prizeRank}º`} {p.prizeValue}
                </Text>
              ))}
            </Space>
          )}

          {(hasLotteryInfo || cycle.spaRegistrationNumber) && (
            <Space direction="vertical" size={2}>
              {hasLotteryInfo && (
                <Text type="secondary">
                  Sorteio Loteria Federal
                  {cycle.lotteryDrawNumber ? ` — Concurso nº ${cycle.lotteryDrawNumber}` : ''}
                  {cycle.lotteryDrawDate ? ` em ${dayjs(cycle.lotteryDrawDate).format('DD/MM/YYYY')}` : ''}
                </Text>
              )}
              {cycle.spaRegistrationNumber && (
                <Text type="secondary">Registro SPA: {cycle.spaRegistrationNumber}</Text>
              )}
            </Space>
          )}
        </Space>
      </Space>
    </Card>
  );
};

export default EngagementPointsCard;
